import { useState, useEffect, useContext, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Button, CircularProgress, IconButton, InputAdornment, Typography } from "@mui/material";
import { Visibility, VisibilityOff, Sync } from "@mui/icons-material";
import { useDempStore } from "src/context/stores";
import { DataSourceMmgService } from "../../services";
import DataSourceContext from '../../context/DataSourceMngContext'
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";


const ServerConfigModal = (props) => {
    const { open, onClose, onSave, defaultUsername = "", defaultPassword = "", serverCode, selectedServer, fetchDataSource } = props;
    const [ plantId, companyId ] = useDempStore((state) => [
        state.plantId,
        state.companyId,
      ]);
    const { registerServer, fetchServerConfig, serverConfig, syncNow } = useContext(DataSourceContext)


    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [timePeriod, setTimePeriod] = useState(1);
    const [url, setUrl] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorTimeout, setErrorTimeout] = useState(null);
    const [cooldown, setCooldown] = useState(0);


    useEffect(() => {
        if (open) {
            setUrl(serverCode?.config?.folder_url || "");
            setTimePeriod(serverCode?.config?.interval || 1);
        }
    }, [open, defaultUsername, defaultPassword, serverCode?.config]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (errorTimeout) {
                clearTimeout(errorTimeout);
            }
        };
    }, [errorTimeout]);

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleFetchServerConfig = async () => {
        const code = selectedServer?.code;
        if (code) {
            try {
                await fetchServerConfig(code);
            } catch (error) {
                console.error("Error fetching server config:", error);
            }
        }
    };

    useEffect(() => {
        const code = selectedServer?.code;
        if (open && code) {
            handleFetchServerConfig();
        }
    }, [selectedServer, open]);

    useEffect(() => {
        if (cooldown === 0 || serverConfig?.wait_minutes === 0) return;
    
        const timer = setInterval(() => {
          setCooldown((prev) => prev - 1);
        }, 1000); // Count down every second
    
        return () => clearInterval(timer);
      }, [cooldown, serverConfig?.wait_minutes]);

    const [syncTriggered, setSyncTriggered] = useState(false);
    const lastWaitMinutesRef = useRef(null);
    const cooldownRef = useRef(0);

    // Update cooldown ref when cooldown state changes
    useEffect(() => {
        cooldownRef.current = cooldown;
    }, [cooldown]);

    useEffect(() => {
        if (serverConfig?.wait_minutes === 0) {
            setCooldown(0);
            lastWaitMinutesRef.current = 0;
            return;
        }
        
        if (serverConfig?.wait_minutes && serverConfig.wait_minutes > 0) {
            const currentWaitMinutes = serverConfig.wait_minutes;
            const waitSeconds = currentWaitMinutes * 60; // Convert to seconds
            
            // If sync was just triggered, always set cooldown
            if (syncTriggered) {
                setCooldown(waitSeconds); // Store as seconds
                setSyncTriggered(false);
                lastWaitMinutesRef.current = currentWaitMinutes;
            } 
            // If wait_minutes changed or cooldown is 0, set it
            else if (lastWaitMinutesRef.current !== currentWaitMinutes || cooldownRef.current === 0) {
                setCooldown(waitSeconds); // Store as seconds
                lastWaitMinutesRef.current = currentWaitMinutes;
            }
        }
    }, [serverConfig?.wait_minutes, syncTriggered]);

    const handleSyncNow = async () => {
        try {
            console.log("sync now");
            setIsSyncing(true);
            setSyncTriggered(true); // Mark that sync was triggered
            
            const serverId = serverConfig?.id;
            const folderUrl = serverCode?.config?.folder_url || url;

            if (!serverId) {
                console.error("Server ID is required");
                setSyncTriggered(false);
                return;
            }
            
            await syncNow(serverId, folderUrl);
            
            // Refresh server config after sync
            if (selectedServer?.code) {
                await fetchServerConfig(selectedServer.code);
            }
            
            if (fetchDataSource) {
                await fetchDataSource();
            }
        } catch (error) {
            console.log("error", error);
            setSyncTriggered(false); // Reset on error
        } finally {
            setIsSyncing(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            let config = {
                service: serverCode?.code,
                company_id: companyId,
                plant_id: plantId,
            };

            if (serverCode?.code === 'huawei') {
                config = {
                    ...config,
                    username: username,
                    password: password
                };
            } else if (serverCode?.code === 'google_drive' || serverCode?.code === 'one_drive') {
                const interval = timePeriod;
                config = {
                    ...config,
                    url: url,
                    interval: interval
                };
            }
            await registerServer(config);
            if (fetchDataSource) {
                await fetchDataSource();
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const getLastSyncText = (lastSyncAt) => {
        if (!lastSyncAt) return 'Never synced';
      
        const lastSync = new Date(lastSyncAt);
        const now = new Date();
      
        const diffMs = now - lastSync;
        if (diffMs < 0) return 'just now';
      
        const diffMinutes = Math.floor(diffMs / 1000 / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);
      
        if (diffMinutes < 1) return 'a few seconds ago';
        if (diffMinutes < 60)
          return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
        if (diffHours < 24)
          return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      };
      

    return (
        <Dialog 
            open={open} 
            onClose={(e) => { e?.stopPropagation?.(); onClose?.(); }} 
            maxWidth="sm" 
            fullWidth
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
                sx: {
                    height: serverCode?.code === 'huawei' ? '500px' : '400px',
                    position: 'relative'
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        width: '100%',
                        flex: 1,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box>
                        {serverCode?.name} Configuration
                        </Box>

                        {serverCode?.code !== 'huawei' && (
                        <Box
                        sx={{
                            fontSize: '12px',
                            fontWeight: 'normal',
                            color: 'text.secondary',
                            mt: 0.5,
                        }}
                        >
                        Last sync <Sync fontSize="10px" /> : {getLastSyncText(serverConfig?.last_manual_sync_at)}
                        </Box>
                        )}
                    </Box>

                    {serverCode?.code !== 'huawei' && (
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            ml: 'auto',
                        }}
                    >
                        <Tooltip title={`Click "Sync Now" to fetch the latest updates.`}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSyncNow();
                                }}
                                disabled={serverConfig?.is_sync_now === false}
                                sx={{
                                    color: serverConfig?.is_sync_now !== false ? 'primary.main' : 'text.disabled',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '50%',
                                    padding: '5px',
                                    boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.1)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                        boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                                    }
                                }}
                            >
                                <Sync fontSize="small" />
                                {/* <Typography variant="caption" sx={{ fontSize: '12px' }}>Sync Now</Typography> */}
                            </IconButton>
                        </Tooltip>

                        {serverConfig?.wait_minutes > 0 && cooldown > 0 && (() => {
                            const minutes = Math.floor(cooldown / 60);
                            const seconds = cooldown % 60;
                            return (
                                <Typography
                                variant="caption"
                                sx={{ 
                                    position: 'absolute',
                                    bottom: '-18px',
                                    left: '60%',
                                    transform: 'translateX(-50%)',
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                    fontSize: '12px',
                                }}
                                >
                                {minutes} m {seconds.toString().padStart(2, '0')} s
                                </Typography>
                            );
                        })()}
                    </Box>
                    )}

                </DialogTitle>

                {serverCode?.code === 'huawei' && (
                <IconButton onClick={(e) => { e.stopPropagation(); onClose?.(); }} size="small" sx={{ marginRight: '10px' }}>
                    <CloseIcon/> 
                </IconButton> 
                )}
            </Box>

            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={40} />
                        {/* <Box sx={{ mt: 2, fontSize: '14px', color: '#666' }}>
                            Loading...
                        </Box> */}
                    </Box>
                </Box>
            )}
            {serverCode?.code === 'huawei' && (
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <Box sx={{ pt: 2 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Username"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleTogglePasswordVisibility}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />
                    
                    {/* Add Account Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2, mb: 2 }} onClick={(e) => e.stopPropagation()}>
                        <Button 
                            onClick={(e) => { e.stopPropagation(); handleSubmit(); }} 
                            variant="contained"
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            Add Account
                        </Button>
                    </Box>
                </Box>

                {/* Display existing accounts if config exists */}
                {serverCode?.config?.accounts && serverCode.config.accounts.length > 0 && (
                    <Box sx={{ pt: 2, px: 2 }} onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ 
                            backgroundColor: '#f5f5f5', 
                            padding: 2, 
                            borderRadius: 1,
                            border: '1px solid #e0e0e0'
                        }}>
                            <Box sx={{ 
                                fontSize: '14px', 
                                fontWeight: 'bold', 
                                color: '#666',
                                mb: 1 
                            }}>
                                Existing Accounts:
                            </Box>
                            {serverCode.config.accounts.map((account, index) => (
                                <Box 
                                    key={index}
                                    sx={{ 
                                        fontSize: '13px', 
                                        color: '#333',
                                        mb: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ 
                                            width: 6, 
                                            height: 6, 
                                            backgroundColor: '#4caf50', 
                                            borderRadius: '50%', 
                                            mr: 1 
                                        }} />
                                        {account.name}
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); }}
                                        sx={{ 
                                            color: '#666',
                                            '&:hover': {
                                                color: '#f44336',
                                                backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                            }
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
                
            </DialogContent>
            )}

            {(serverCode?.code === 'google_drive' || serverCode?.code === 'one_drive') && (
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <Box sx={{ pt: 2 }}>
                     <TextField
                         autoFocus
                         margin="dense"
                         label="URL Link"
                         type="text"
                         fullWidth
                         variant="outlined"
     
                         value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        sx={{ mb: 2 }}
                     />
                     <TextField
                         margin="dense"
                         label="Time Period"
                         select
                         fullWidth
                         variant="outlined"
                         value={timePeriod}
                         onChange={(e) => setTimePeriod(e.target.value)}
                         SelectProps={{
                             native: true,
                         }}
                         sx={{ mb: 2 }}
                     >
                         <option value="1">1 Day</option>
                         <option value="30">1 Month</option>
                     </TextField>
                     
                
                     {/* Error Message Display */}
                     {errorMessage && (
                         <Box 
                             sx={{ 
                                 mt: 1, 
                                 p: 1.5, 
                                 backgroundColor: '#ffebee', 
                                 border: '1px solid #f44336',
                                 borderRadius: 1,
                                 color: '#c62828',
                                 fontSize: '13px'
                             }}
                         >
                             {errorMessage}
                         </Box>
                     )}
                 </Box>
            </DialogContent>
            )}
            {serverCode?.code !== 'huawei' && (
                <DialogActions onClick={(e) => e.stopPropagation()}>
                    <Button 
                        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
                        sx={{ 
                            border: '1px solid #f44336', 
                            color: '#f44336',
                            '&:hover': {
                                border: '1px solid #d32f2f',
                                backgroundColor: '#ffebee'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={(e) => { e.stopPropagation(); handleSubmit(); }} 
                        variant="contained"
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isLoading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ServerConfigModal;


